import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import { Notification, NotificationStatus, NotificationType } from './notifications.models';
import * as notificationsActions from './notifications.actions';
import { QueryParams } from '../../core/store/models';

export const notificationsModuleKey = 'notifications';
export const notificationsFeatureKey = 'notifications';

export interface NotificationsModuleState {
  notifications: NotificationsState;
}

export interface NotificationsState {
  hoverNotifications: Notification[];
  unreadNotifications: Notification[];
  unreadQueryParams: QueryParams;
  showNotifications: boolean;
  loading: boolean;
}

export const initialNotificationsState: NotificationsState = {
  hoverNotifications: [],
  unreadNotifications: [],
  unreadQueryParams: { // params are literally only being used for total count. notifications needs to be refactored to not be so over-engineered
    skip: 0,
    take: 50,
    sortField: 'DateSent',
    sortDirection: 'Descending',
    totalCount: 0
  },
  showNotifications: false,
  loading: false
};

export const reducer: ActionReducer<NotificationsState, Action> = createReducer(
  initialNotificationsState,
  on(notificationsActions.loadUnreadNotifications, state => ({
    ...state,
    loading: true
  })),
  on(notificationsActions.loadUnreadNotificationsComplete, (state, action) => {
    // -- TOPO1-5420 : using this to persist the notifications coming from map layers with includeInNotifications enabled
    const unreadNotifications = [...state.unreadNotifications.filter(n => n.isTransient), ...(action.data?.data ?? [])].sort((a, b) => {
      const aDate = new Date(a.dateSent);
      const bDate = new Date(b.dateSent);
      return bDate.valueOf() - aDate.valueOf();
    });

    return {
      ...state,
      unreadQueryParams: {
        ...state.unreadQueryParams,
        // -- these variables never get mutated by the backend or the ui, only thing of value is totalCount --
        // -- leaving commented out for archival/discovery reasons --
        // -- Issues arose with params getting reset when changing orgs after moving notifications to be application-wide --
        // skip: action.data?.skip,
        // take: action.data?.take,
        // where: action.data?.where,
        // sortField: action.data?.sortField,
        // sortDirection: action.data?.sortDirection,
        totalCount: action.data?.totalCount
      },
      // unreadNotifications: action.data?.data ?? [],
      unreadNotifications,
      loading: false
    };
  }),
  on(notificationsActions.addUnreadNotification, (state, action) => {
    return {
      ...state,
      hoverNotifications: [...state.hoverNotifications, action.notification],
      unreadNotifications: [action.notification, ...state.unreadNotifications],
      unreadQueryParams: {
        ...state.unreadQueryParams,
        totalCount: state.unreadQueryParams.totalCount + 1
      }
    };
  }),
  on(notificationsActions.dismissHoverNotificationComplete, (state, action) => {
    const uId = action.uId;
    const hoverNotifications = state.hoverNotifications.filter(n => n.uId !== uId);
    const unreadNotifications = action.markRead ? state.unreadNotifications.filter(n => n.uId !== uId) : state.unreadNotifications;

    return {
      ...state,
      hoverNotifications,
      unreadNotifications
    };
  }),
  on(notificationsActions.showNotifications, (state, action) => {
    return {
      ...state,
      showNotifications: true
    };
  }),
  on(notificationsActions.hideNotifications, (state, action) => {
    return {
      ...state,
      showNotifications: false
    };
  }),
  on(notificationsActions.clearTransientNotification, (state, action) => {
    return {
      ...state,
      hoverNotifications: state.hoverNotifications.filter(n => n.uId !== action.uId),
      unreadNotifications: state.unreadNotifications.filter(n => n.uId !== action.uId)
    };
  }),
  on(notificationsActions.clearAllNotificationsComplete, (state, action) => {
    return {
      ...state,
      hoverNotifications: [],
      unreadNotifications: [],
      unreadQueryParams: {
        skip: 0,
        take: 50,
        where: null,
        sortField: 'DateSent',
        sortDirection: 'Descending',
        sort: null,
        include: null,
        totalCount: 0
      }
    };
  })
);

export const getHoverNotifications = (state: NotificationsState) => state?.hoverNotifications;
export const getUnreadNotifications = (state: NotificationsState) => state?.unreadNotifications;
export const getUnreadQueryParams = (state: NotificationsState) => state?.unreadQueryParams;
export const getShowNotifications = (state: NotificationsState) => state?.showNotifications;
export const getNotificationsLoading = (state: NotificationsState) => state?.loading;
